import AppIcon from "../components/AppIcon";
import { use, useEffect, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { LuSearch as CiSearch } from "react-icons/lu";
import { LuChevronRight as FiChevronRight, LuPlus as FiPlus } from "react-icons/lu";
import { database } from "../constants/firebase";
import { ref, onValue, off, set } from "firebase/database";
import { ROUTES } from "../App";
import useMoveTo from "../constants/navigation";
import { useUser } from "../contexts/UserContext";
import { formatDate, formatPrice } from "../utils/utils";
import { useParams } from "react-router";
import MobileHeader from "../components/MobileHeader";

const StoreProducts = () => {
    const navigate = useMoveTo();
    const { currentUser } = useUser();
    const { category } = useParams();
    const [searchQuery, setSearchQuery] = useState("");
    const [products, setProducts] = useState([]);
    const [users, setUsers] = useState({});
    const [activeTab, setActiveTab] = useState("By Rating");
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, link: "" });
    const [loadingImage, setLoadingImage] = useState(true);
    const [loading, setLoading] = useState(true);

    const [exchangeRates, setExchangeRates] = useState({ USD_TO_ZAR: 18.5, ZAR_TO_USD: 0.054 }); // Default rates
    const [userCountry, setUserCountry] = useState(""); // Will store user's country


    // ✅ Fetch exchange rates from an API (using free API)
    useEffect(() => {
        const fetchExchangeRates = async () => {
            try {
                // You can use a free exchange rate API
                const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
                const data = await response.json();
                if (data.rates && data.rates.ZAR) {
                    setExchangeRates({
                        USD_TO_ZAR: data.rates.ZAR,
                        ZAR_TO_USD: 1 / data.rates.ZAR
                    });
                }
            } catch (error) {
                console.error("Failed to fetch exchange rates, using defaults:", error);
                // Keep default rates if API fails
            }
        };

        fetchExchangeRates();
    }, []);

    // ✅ Determine user's country
    useEffect(() => {
        const determineUserCountry = () => {
            // Method 1: From user profile if available
            if (currentUser?.country) {
                setUserCountry(currentUser.country);
                return currentUser.country;
            }
        };

        determineUserCountry();
    }, [currentUser]);

    const getConvertedPrice = (product) => {
        if (!product?.price) return { price: 0, currency: "" };

        const price = parseFloat(product.price);
        const productCurrency = (product.currency || "").trim().toUpperCase();

        let calculatedPrice = price;
        let calculatedCurrency = productCurrency;

        if (userCountry === "South Africa") {
            if (productCurrency === "USD") {
                calculatedPrice = price * exchangeRates.USD_TO_ZAR;
                calculatedCurrency = "ZAR";
            }
        } else {
            if (productCurrency === "ZAR") {
                calculatedPrice = price * exchangeRates.ZAR_TO_USD;
                calculatedCurrency = "USD";
            }
        }

        return {
            price: calculatedPrice,
            currency: calculatedCurrency
        };
    };



    useEffect(() => {
        const closeMenu = () => setContextMenu({ ...contextMenu, visible: false });
        window.addEventListener("click", closeMenu);
        return () => window.removeEventListener("click", closeMenu);
    }, [contextMenu]);

    // ✅ Load all users
    useEffect(() => {
        const usersRef = ref(database, "Users");
        const unsubscribe = onValue(usersRef, (snapshot) => {
            if (snapshot.exists()) {
                setUsers(snapshot.val());
            }
        });

        return () => off(usersRef, "value", unsubscribe);
    }, []);

    // ✅ Load courses
    useEffect(() => {
        setLoading(true);
        const coursesRef = ref(database, "Listings");

        const unsubscribe = onValue(coursesRef, (snapshot) => {
            if (snapshot.exists()) {
                if (category) {
                    const data = snapshot.val();
                    const all = Object.keys(data).map((id) => ({ id, ...data[id] }));


                    // ✅ Only published products
                    const filtered = all.filter(
                        (item) => item.type === "product" && item.category === category && item.status === "Published"
                    );

                    // ✅ Sort by createdAt (descending)
                    const sortedProducts = filtered.sort(
                        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                    );

                    setProducts(sortedProducts);
                    setLoading(false);
                }
            }
        });
        return () => off(coursesRef, "value", unsubscribe);
    }, []);

    // ✅ Search filter
    const filteredProducts = products.filter((product) => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
            product.title?.toLowerCase().includes(q) ||
            product.explainer?.toLowerCase().includes(q) ||
            (users && users[product.userId]?.name?.toLowerCase().includes(q)) // 👈 search by creator name too
        );
    });

    // 🟢 Simple shimmer skeleton loader component
    const ShimmerLoader = () => (
        <div className="flex flex-col space-y-4 mt-8">
            {[...Array(6)].map((_, i) => (
                <div
                    key={i}
                    className="animate-pulse flex items-center justify-between p-4 border-t border-gray-200 dark:border-[#D9D9D9]/20"
                >
                    <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gray-300 dark:bg-gray-700 rounded-lg"></div>
                        <div>
                            <div className="w-12 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                            <div className="w-40 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                            <div className="w-20 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                        </div>
                    </div>
                    <div className="w-5 h-6 bg-gray-300 dark:bg-gray-700 rounded"></div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="h-screen flex flex-col bg-white dark:bg-[#23272c] text-black dark:text-white">
            {/* Mobile Header */}
            <MobileHeader title={category || "Products"} isBackBtn={true} />


            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 min-h-0 overflow-y-auto pt-5 pb-24 md:pb-20 px-6">
                    {/* Title */}
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <AppIcon name="arrowBack" className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">{category || "Products"}</h2>
                        <div className="w-5" />
                    </div>

                    {/* Tabs */}
                    {/* <div className="flex gap-2 mt-8 mb-2">
                        {['By Rating', 'By Niche', 'By Creator'].map(tab => (
                            <button key={tab} onClick={() => setActiveTab(tab)}
                                className={`flex-1 px-0 py-2 rounded-lg font-semibold transition min-w-0 
                            ${activeTab === tab ? 'bg-[#327691] text-white' : 'bg-transparent text-black dark:text-white'}`}
                            >
                                {tab}
                            </button>))}
                    </div> */}

                    {/* Search */}
                    <div className="relative w-full md:mt-6 mt-0">
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="rounded-xl pl-5 pr-10 py-3 bg-[#F5F9FE] dark:bg-white 
                                       placeholder-[#6b7280] outline-none w-full text-[#262626] 
                                       dark:text-[#262626] border border-gray-200 dark:border-none"
                        />
                        <CiSearch
                            className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-500 text-2xl"
                        />
                    </div>

                    {/* Product List */}
                    <div className="mt-6 space-y-4">
                        {loading ? (
                            <ShimmerLoader />
                        ) : filteredProducts.length === 0 ? (
                            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                                No published products found.
                            </div>
                        ) : (
                            filteredProducts.map((item) => {
                                const creator = users ? users[item.userId]?.name : "Unknown Creator"; // 👈 get name from Users
                                const creatorData = users ? users[item.userId] : null;
                                return (
                                    <div
                                        key={item.id}
                                        className="relative flex items-center justify-between border-t text-black 
                   dark:border-[#D9D9D9]/20 border-gray-200 dark:text-white px-4 py-4 
                   hover:bg-gray-100 dark:hover:bg-[#2A2A2A] transition-colors duration-200 cursor-pointer"
                                        onClick={() => {
                                            if (currentUser) {
                                                navigate(`${ROUTES.PRODUCT_DETAILS}/${item.id}`);
                                            } else {
                                                navigate(`${ROUTES.PREVIEW_PRODUCT_DETAILS}/${item.id}`);
                                            }
                                        }}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            if (currentUser) {
                                                setContextMenu({
                                                    visible: true,
                                                    x: e.pageX,
                                                    y: e.pageY,
                                                    link: `${window.location.origin}/#${ROUTES.PRODUCT_DETAILS}/${item.id}`
                                                });
                                            }
                                        }}
                                    >
                                        <div className="flex items-center gap-4 max-w-[60%]">
                                            {/* Spinner while loading */}
                                            <div className="relative w-16 h-16 flex-shrink-0">
                                                {!item.imageLoaded && (
                                                    <div className="absolute inset-0 bg-gray-300 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                                                )}
                                                <img
                                                    src={item?.coverImageUrl || IMAGES.courses}
                                                    alt={item.title}
                                                    className={`w-16 h-16 rounded-lg object-cover transition-opacity duration-300 ${item.imageLoaded ? "opacity-100" : "opacity-0"
                                                        }`}
                                                    onLoad={() => {
                                                        item.imageLoaded = true;
                                                        setProducts((prev) => [...prev]); // or setCourses for courses page
                                                    }}
                                                    onError={() => {
                                                        item.imageLoaded = true;
                                                        setProducts((prev) => [...prev]); // or setCourses
                                                    }}
                                                />
                                            </div>

                                            <div>
                                                <p className="text-sm text-gray-700 dark:text-white/80 line-clamp-2">
                                                    {formatDate(item.createdAt)}
                                                </p>
                                                <h3 className="font-bold text-lg line-clamp-1">{item.title}</h3>

                                                <p className="text-xs text-gray-500 dark:text-white/50 mt-1 flex items-center gap-1">
                                                    By
                                                    <span
                                                        className={`flex items-center gap-1 font-semibold cursor-pointer ${creatorData?.role === "Host"
                                                            ? "dark:glow-text-dark glow-text-light"
                                                            : "text-gray-500 dark:text-white/50"
                                                            }`}
                                                    >
                                                        {creator}
                                                        {creatorData?.isTeamUser && (
                                                            <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#327691">
                                                                <path d="M10.5213 2.62368C11.3147 1.75255 12.6853 1.75255 13.4787 2.62368L14.4989 3.74391C14.8998 4.18418 15.4761 4.42288 16.071 4.39508L17.5845 4.32435C18.7614 4.26934 19.7307 5.23857 19.6757 6.41554L19.6049 7.92905C19.5771 8.52388 19.8158 9.10016 20.2561 9.50111L21.3763 10.5213C22.2475 11.3147 22.2475 12.6853 21.3763 13.4787L20.2561 14.4989C19.8158 14.8998 19.5771 15.4761 19.6049 16.071L19.6757 17.5845C19.7307 18.7614 18.7614 19.7307 17.5845 19.6757L16.071 19.6049C15.4761 19.5771 14.8998 19.8158 14.4989 20.2561L13.4787 21.3763C12.6853 22.2475 11.3147 22.2475 10.5213 21.3763L9.50111 20.2561C9.10016 19.8158 8.52388 19.5771 7.92905 19.6049L6.41553 19.6757C5.23857 19.7307 4.26934 18.7614 4.32435 17.5845L4.39508 16.071C4.42288 15.4761 4.18418 14.8998 3.74391 14.4989L2.62368 13.4787C1.75255 12.6853 1.75255 11.3147 2.62368 10.5213L3.74391 9.50111C4.18418 9.10016 4.42288 8.52388 4.39508 7.92905L4.32435 6.41553C4.26934 5.23857 5.23857 4.26934 6.41554 4.32435L7.92905 4.39508C8.52388 4.42288 9.10016 4.18418 9.50111 3.74391L10.5213 2.62368Z" stroke="#327691" strokeWidth="1.5"></path>
                                                                <path d="M9 12L11 14L15 10" stroke="#327691" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                                            </svg>
                                                        )}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right min-w-[60px] flex flex-col items-end justify-center gap-1">
                                            {item.price && (
                                                <p className="font-bold text-lg text-[#327691]">
                                                    {(() => {
                                                        const converted = getConvertedPrice(item);
                                                        return formatPrice(converted.price, converted.currency, item.currency);
                                                    })()}
                                                </p>
                                            )}
                                            <FiChevronRight className="text-gray-700 dark:text-white text-xl" />
                                        </div>

                                    </div>
                                );
                            })
                        )}
                    </div>

                    {contextMenu.visible && (
                        <div
                            className="absolute z-50 bg-white dark:bg-[#2A2A2A] border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-2"
                            style={{ top: contextMenu.y, left: contextMenu.x }}
                        >
                            <button
                                className="block w-full text-left px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-[#3A3A3A] rounded"
                                onClick={() => {
                                    navigator.clipboard.writeText(contextMenu.link);
                                    window.showToast("Link copied to clipboard!", "success");
                                    setContextMenu({ ...contextMenu, visible: false });
                                }}
                            >
                                Copy Link
                            </button>
                        </div>
                    )}


                </main>
            </div >
        </div >
    );
};

export default StoreProducts;
